import { ToastContext } from "context/ToastContext";
import { useCurrentAccount } from "hooks/useCurrentAccount";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { useContext, useState } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useExpandRateFileMutation } from "store/queries/rateTools";
import styled from "styled-components";

import { LIBRARY_CLAIM_TYPE_OPTIONS } from "../utils/constants";
import { ToolFilePicker } from "./ToolFilePicker";
import { IExpandStats, parseExpandStats } from "./utils";

/**
 * CLE-2331 — Rate Expander sub-tab on PayerRules → Tools.
 *
 * Two uploads (base rate file + rules sheet) + a payer selector + a claim-type
 * picker. Hits the expander endpoint, downloads the per-CPT expanded CSV, and
 * surfaces the list of unmapped service lines (if any) with a CTA pointing the
 * analyst at the Synonyms sub-tab.
 *
 * `accountId` is the current account from `useCurrentAccount`; `payerId` is required
 * because the synonym-dict lookup is per-(account, payer). The `claimType` field is
 * a CLE-2323 follow-on: it scopes the per-payer service-line library lookup the
 * expander uses as a fallback when the base file's Service Line column is empty.
 */

type ClaimType = "PROFESSIONAL" | "OUTPATIENT" | "INPATIENT";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 720px;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  label {
    font-size: 0.85em;
    color: var(--gray-700);
  }
`;

const SubmitRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

interface IRatesValidationError {
  message: string;
  errors: string[];
}

export function RateExpander({
  onNavigateToSynonyms,
}: {
  onNavigateToSynonyms: (payerId?: number) => void;
}) {
  const { current: toastElement } = useContext(ToastContext);
  const { accountInfo } = useCurrentAccount();
  const { data: payersData } = useGetAllPayersQuery();

  const [baseFile, setBaseFile] = useState<File | null>(null);
  const [rulesFile, setRulesFile] = useState<File | null>(null);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [claimType, setClaimType] = useState<ClaimType>("PROFESSIONAL");

  const [lastStats, setLastStats] = useState<IExpandStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[] | null>(
    null
  );

  const [expand, { isLoading }] = useExpandRateFileMutation();

  const payerOptions = (payersData?.data ?? []).map((p: any) => ({
    label: p.name,
    value: p.id,
  }));

  const accountId = accountInfo?.id;
  const submitDisabled =
    !baseFile || !rulesFile || !accountId || payerId == null || isLoading;

  // CLE-2885 — removing either file (the picker's ×) also clears the last run's error,
  // which described the files being replaced. The last run's stats banner stays.
  const clearRunError = () => {
    setErrorMessage(null);
    setValidationErrors(null);
  };

  const onBaseFileChange = (next: File | null) => {
    setBaseFile(next);
    if (!next) clearRunError();
  };

  const onRulesFileChange = (next: File | null) => {
    setRulesFile(next);
    if (!next) clearRunError();
  };

  const submit = async () => {
    if (!baseFile || !rulesFile || !accountId || payerId == null) return;
    setLastStats(null);
    setErrorMessage(null);
    setValidationErrors(null);

    try {
      const result = await expand({
        baseFile,
        rulesFile,
        accountId,
        payerId,
        claimType,
      }).unwrap();

      setLastStats(parseExpandStats(result.headers));

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      const inferredName = (baseFile.name || "base").replace(/\.[^.]+$/, "");
      link.download = `${inferredName}-expanded.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toastElement?.show({
        severity: "success",
        summary: "Expand complete",
        detail: "Your CSV is downloading.",
      });

      // Clear the file inputs so the next run is a fresh upload.
      setBaseFile(null);
      setRulesFile(null);
    } catch (e: any) {
      // 400 with `errors[]` is a rules-sheet validation error — surface each row.
      const data = e?.data;
      if (data && Array.isArray((data as IRatesValidationError).errors)) {
        setValidationErrors((data as IRatesValidationError).errors);
        setErrorMessage(
          (data as IRatesValidationError).message ??
            "Rules sheet has validation errors."
        );
        return;
      }
      const detail =
        data?.message ??
        data?.error ??
        (typeof data === "string" ? data : null) ??
        e?.message ??
        "Expand failed.";
      setErrorMessage(detail);
    }
  };

  return (
    <Wrapper>
      <h3>Expand Rate File</h3>
      <p style={{ margin: 0, color: "var(--gray-600)" }}>
        Multiply a base rate file by a small Service-Line / Multiplier-% rules
        sheet to produce a per-CPT expanded rate file. Service lines that
        don&apos;t match the rules sheet are kept in the output at multiplier 1
        (rate = base rate) and flagged with <code>Unmapped=true</code>; their
        labels are listed below so you can add them to the synonyms dictionary
        or fix the rules sheet.
      </p>
      <details style={{ color: "var(--gray-700)", fontSize: "0.9em" }}>
        <summary style={{ cursor: "pointer" }}>Expected columns</summary>
        <p style={{ margin: "0.5rem 0 0.25rem 0" }}>
          <strong>Base rate file:</strong>
        </p>
        <ul style={{ margin: "0 0 0.5rem 1.2rem", padding: 0 }}>
          <li>
            <strong>CPT code</strong> (required) — header can be any of:{" "}
            <code>CPT Code</code>, <code>CPT/HCPCS</code>,{" "}
            <code>Procedure Code</code>, <code>HCPCS</code>,{" "}
            <code>Proc_CD</code>, <code>CPT</code>
          </li>
          <li>
            <strong>Rate</strong> (required) — any of: <code>Rate</code>,{" "}
            <code>Fee Amount</code>, <code>Base Rate</code>,{" "}
            <code>Calculated rate</code>
          </li>
          <li>
            <strong>Service Line / Type of Service</strong> (optional — if
            missing, the rate expander falls back to the per-payer Service Line
            Library)
          </li>
          <li>
            <strong>MOD / Modifier</strong>, <strong>POS</strong>,{" "}
            <strong>Description</strong> (optional, passed through to the
            output)
          </li>
        </ul>
        <p style={{ margin: "0.5rem 0 0.25rem 0" }}>
          <strong>Rules sheet:</strong>
        </p>
        <ul style={{ margin: "0 0 0 1.2rem", padding: 0 }}>
          <li>
            <strong>Service Line</strong> (required) — any of:{" "}
            <code>Service Line</code>, <code>Service Lines</code>,{" "}
            <code>Specialty</code>, <code>Category</code>
          </li>
          <li>
            <strong>Multiplier %</strong> (required) — any of:{" "}
            <code>Multiplier %</code>, <code>Multiplier</code>,{" "}
            <code>Multiplier Percent</code>, <code>Percent</code>,{" "}
            <code>%</code>. Values can be decimal (<code>1.74</code>) or percent
            (<code>174</code>); 0 is valid, blanks/negatives are not.
          </li>
        </ul>
        <p style={{ margin: "0.5rem 0 0 0" }}>
          Hyphens and underscores in header names are treated as spaces (so{" "}
          <code>Service-Line</code> and <code>Multiplier-%</code> both work).
          Rules-sheet labels must match the base file&apos;s Service Line values
          (or be bridged via the Service Line Synonyms admin sub-tab).
        </p>
      </details>

      <Row>
        <div style={{ minWidth: 240 }}>
          <label htmlFor="expander-payer">
            Payer (scopes synonym dict + library)
          </label>
          <Dropdown
            inputId="expander-payer"
            value={payerId}
            options={payerOptions}
            onChange={(e) => setPayerId(e.value ?? null)}
            placeholder="Select payer"
            filter
            disabled={isLoading}
          />
        </div>
        <div style={{ minWidth: 200 }}>
          <label htmlFor="expander-claim-type">
            Claim type (scopes library)
          </label>
          <Dropdown
            inputId="expander-claim-type"
            value={claimType}
            options={[...LIBRARY_CLAIM_TYPE_OPTIONS]}
            onChange={(e) => setClaimType(e.value)}
            disabled={isLoading}
          />
        </div>
      </Row>

      <Row>
        <div>
          <label>Base rate file (CSV or XLSX)</label>
          <ToolFilePicker
            file={baseFile}
            onChange={onBaseFileChange}
            chooseLabel="Choose base file"
            disabled={isLoading}
          />
        </div>
        <div>
          <label>Rules sheet (Service Line | Multiplier %)</label>
          <ToolFilePicker
            file={rulesFile}
            onChange={onRulesFileChange}
            chooseLabel="Choose rules sheet"
            disabled={isLoading}
          />
        </div>
      </Row>

      <SubmitRow>
        <Button
          label="Expand"
          icon="pi pi-arrow-right"
          onClick={submit}
          disabled={submitDisabled}
        />
      </SubmitRow>

      {isLoading && (
        <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
      )}

      {errorMessage && (
        <Message
          severity="error"
          content={
            <div>
              <div>{errorMessage}</div>
              {validationErrors && validationErrors.length > 0 && (
                <ul style={{ margin: "0.5rem 0 0 1.2rem", padding: 0 }}>
                  {validationErrors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          }
          style={{ alignItems: "flex-start" }}
        />
      )}

      {lastStats && !errorMessage && (
        <Message
          severity={lastStats.unmappedRows > 0 ? "warn" : "success"}
          content={
            <div>
              <div>
                <strong>
                  {lastStats.mappedRows.toLocaleString()} rows mapped
                  {lastStats.unmappedRows > 0 &&
                    `, ${lastStats.unmappedRows.toLocaleString()} unmapped (kept at multiplier 1 — see below)`}
                </strong>
              </div>
              <div>
                {lastStats.inputRows.toLocaleString()} input rows
                {(lastStats.droppedMissingCpt > 0 ||
                  lastStats.droppedNoBaseRate > 0) &&
                  ` (dropped ${lastStats.droppedMissingCpt} no-CPT + ${lastStats.droppedNoBaseRate} no-base-rate)`}
              </div>
              {lastStats.resolvedFromLibrary > 0 && (
                <div style={{ color: "var(--gray-700)", fontSize: "0.9em" }}>
                  {lastStats.resolvedFromLibrary.toLocaleString()} row
                  {lastStats.resolvedFromLibrary === 1 ? "" : "s"} had Service
                  Line filled in from the per-payer library.
                </div>
              )}
              {lastStats.unmappedServiceLines.length > 0 && (
                <div style={{ marginTop: "0.5rem" }}>
                  <div style={{ marginBottom: "0.25rem" }}>
                    These service lines from the base file weren&apos;t matched
                    by your rules sheet (and no synonym dictionary entry exists
                    either):
                  </div>
                  <ul style={{ margin: "0 0 0.5rem 1.2rem" }}>
                    {lastStats.unmappedServiceLines
                      .slice(0, 20)
                      .map((sl, idx) => (
                        <li key={idx}>
                          <code>{sl}</code>
                        </li>
                      ))}
                    {lastStats.unmappedServiceLines.length > 20 && (
                      <li>
                        <em>
                          …and {lastStats.unmappedServiceLines.length - 20} more
                          (see the downloaded CSV)
                        </em>
                      </li>
                    )}
                  </ul>
                  <Button
                    label="Open Synonyms sub-tab"
                    icon="pi pi-arrow-right"
                    className="p-button-sm p-button-outlined"
                    onClick={() => onNavigateToSynonyms(payerId ?? undefined)}
                  />
                </div>
              )}
            </div>
          }
          style={{ alignItems: "flex-start" }}
        />
      )}
    </Wrapper>
  );
}

export default RateExpander;
